// src/redux/slices/adminMessageSlice.js

import { createSlice, createAsyncThunk } from "@reduxjs/toolkit";
import api from "../../services/api";


// ========================
// 1️⃣ Send Admin Group Message
// ========================
export const sendMessageToGroup = createAsyncThunk(
    "admin/sendMessageToGroup",
    async (payload, { rejectWithValue }) => {
        try {
            const res = await api.post("/admin/send-message", payload);
            return res.data;
        } catch (err) {
            return rejectWithValue(err.response?.data || err.message);
        }
    }
);


// ========================
// 2️⃣ Get All Messages Sent by Admin
// ========================
export const getAllAdminMessages = createAsyncThunk(
    "admin/getAllAdminMessages",
    async (page = 1, { rejectWithValue }) => {
        try {
            const res = await api.get(`/admin/all-messages?page=${page}`);
            return res.data;
        } catch (err) {
            return rejectWithValue(err.response?.data || err.message);
        }
    }
);


// ========================
// 3️⃣ Update Admin Message
// ========================
export const updateAdminMessage = createAsyncThunk(
    "admin/updateAdminMessage",
    async ({ id, payload }, { rejectWithValue }) => {
        try {
            const res = await api.put(`/messages/${id}`, payload);
            return res.data;
        } catch (err) {
            return rejectWithValue(err.response?.data || err.message);
        }
    }
);


// ========================
// 4️⃣ Delete Admin Message
// ========================
export const deleteAdminMessage = createAsyncThunk(
    "admin/deleteAdminMessage",
    async (id, { rejectWithValue }) => {
        try {
            const res = await api.delete(`/messages/${id}`);
            return res.data;
        } catch (err) {
            return rejectWithValue(err.response?.data || err.message);
        }
    }
);


// ========================
// Slice
// ========================
const adminMessageSlice = createSlice({
    name: "adminMessages",
    initialState: {
        sending: false,
        loading: false,
        messages: [],
        pagination: {},
        error: null,
        successMessage: null,
    },

    reducers: {
        clearAdminMessageState: (state) => {
            state.error = null;
            state.successMessage = null;
        },
    },

    extraReducers: (builder) => {
        builder

            // SEND MESSAGE
            .addCase(sendMessageToGroup.pending, (state) => {
                state.sending = true;
                state.error = null;
            })
            .addCase(sendMessageToGroup.fulfilled, (state, action) => {
                state.sending = false;
                state.successMessage = action.payload.message;
            })
            .addCase(sendMessageToGroup.rejected, (state, action) => {
                state.sending = false;
                state.error = action.payload?.message || "Something went wrong";
            })


            // GET MESSAGES
            .addCase(getAllAdminMessages.pending, (state) => {
                state.loading = true;
            })
            .addCase(getAllAdminMessages.fulfilled, (state, action) => {
                state.loading = false;
                state.messages = action.payload.data || [];
                state.pagination = action.payload.pagination || {};
            })
            .addCase(getAllAdminMessages.rejected, (state, action) => {
                state.loading = false;
                state.error = action.payload?.message || "Something went wrong";
            })


            // UPDATE MESSAGE
            .addCase(updateAdminMessage.pending, (state) => {
                state.sending = true;
            })
            .addCase(updateAdminMessage.fulfilled, (state, action) => {
                state.sending = false;
                state.successMessage = action.payload.message;

                // Update list instantly
                const updated = action.meta.arg;
                state.messages = state.messages.map((msg) =>
                    msg._id === updated.id ? { ...msg, ...updated.payload } : msg
                );
            })
            .addCase(updateAdminMessage.rejected, (state, action) => {
                state.sending = false;
                state.error = action.payload?.message || "Update failed";
            })


            // DELETE MESSAGE
            .addCase(deleteAdminMessage.pending, (state) => {
                state.loading = true;
            })
            .addCase(deleteAdminMessage.fulfilled, (state, action) => {
                state.loading = false;
                state.successMessage = action.payload.message;

                const deletedId = action.meta.arg;
                state.messages = state.messages.filter((msg) => msg._id !== deletedId);
            })
            .addCase(deleteAdminMessage.rejected, (state, action) => {
                state.loading = false;
                state.error = action.payload?.message || "Delete failed";
            });
    }
});

export const { clearAdminMessageState } = adminMessageSlice.actions;
export default adminMessageSlice.reducer;
